// ===================================================================
// 👤 REGISTRO DE CANDIDATO - IMPLEMENTAÇÃO COMPLETA
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { EmailInboxHint } from '../../components/auth/EmailInboxHint';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { syncCandidatePwaBranding } from '../../pwa/candidatePwa';
import { validateNewPassword, PASSWORD_POLICY_HINT } from '../../utils/passwordPolicy';

export const CandidateRegisterScreen = React.memo(({ setView, registrationToken, onRegistrationComplete }) => {
  const [systemName, setSystemName] = useState('Ingressa');
  const [icon, setIcon] = useState('');
  const [termsOfUse, setTermsOfUse] = useState('');
  const [step, setStep] = useState(registrationToken ? 'complete' : 'email');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [devConfirmationUrl, setDevConfirmationUrl] = useState('');
  const [activeToken, setActiveToken] = useState(registrationToken || '');
  const [tokenValidating, setTokenValidating] = useState(!!registrationToken);
  const [registrationBlocked, setRegistrationBlocked] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    mother_name: '',
    father_name: '',
    social_name: '',
    use_social_name: false,
    birth_date: '',
    cpf: '',
    identity_type: 'CI',
    identity_number: '',
    issuing_org: '',
    issuing_date: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    apiClient.getConfig().then((d) => {
      if (d.is_platform_owner) {
        setRegistrationBlocked(true);
        return;
      }
      setSystemName(d.system_name || 'Ingressa');
      setIcon(d.institution_icon || '');
      setTermsOfUse(d.terms_of_use || '');
      syncCandidatePwaBranding(d);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!registrationToken) return undefined;
    let cancelled = false;
    (async () => {
      setTokenValidating(true);
      setError('');
      const { data, error: verifyError } = await apiClient.registerVerifyToken(registrationToken);
      if (cancelled) return;
      if (verifyError || !data?.valid) {
        setError(verifyError || 'Link inválido ou expirado. Solicite um novo e-mail de cadastro.');
        setStep('email');
        setActiveToken('');
      } else {
        setFormData((prev) => ({ ...prev, email: data.email || '' }));
        setActiveToken(registrationToken);
        setStep('complete');
      }
      setTokenValidating(false);
    })();
    return () => { cancelled = true; };
  }, [registrationToken]);

  /** Dados pessoais e endereço em caixa alta; e-mail em minúsculas; senhas sem alteração. */
  const normalizeCandidateRegisterFieldValue = useCallback((field, value) => {
    if (value === null || value === undefined) return value;
    if (field === 'email') return String(value).toLowerCase();
    if (field === 'password' || field === 'confirmPassword' || field === 'use_social_name') return value;
    if (field === 'birth_date' || field === 'issuing_date') return value;
    if (field === 'identity_type') return value;
    if (field === 'cpf' || field === 'phone' || field === 'address.zipCode') return value;
    return String(value).toUpperCase();
  }, []);

  const handleInputChange = useCallback((field, value) => {
    const next = normalizeCandidateRegisterFieldValue(field, value);
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: next
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: next }));
    }
    if (error) setError('');
  }, [error, normalizeCandidateRegisterFieldValue]);

  const validateCPF = useCallback((cpf) => {
    cpf = cpf.replace(/[^\d]/g, '');
    if (cpf.length !== 11) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cpf)) return false;
    
    // Validação do primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.charAt(9))) return false;
    
    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cpf.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpf.charAt(10))) return false;
    
    return true;
  }, []);

  const validateEmailStep = useCallback(() => {
    const email = formData.email.trim().toLowerCase();
    const confirm = emailConfirm.trim().toLowerCase();
    if (!email) {
      setError('E-mail é obrigatório');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('E-mail inválido');
      return false;
    }
    if (!confirm) {
      setError('Confirme o e-mail');
      return false;
    }
    if (email !== confirm) {
      setError('Os e-mails informados não coincidem');
      return false;
    }
    return true;
  }, [formData.email, emailConfirm]);

  const validateForm = useCallback(() => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return false;
    }
    
    if (!formData.cpf.trim()) {
      setError('CPF é obrigatório');
      return false;
    }

    if (!formData.birth_date.trim()) {
      setError('Data de nascimento é obrigatória');
      return false;
    }

    if (!formData.mother_name.trim()) {
      setError('Nome da mãe é obrigatório');
      return false;
    }

    if (!validateCPF(formData.cpf)) {
      setError('CPF inválido');
      return false;
    }
    
    if (!formData.identity_number.trim()) {
      setError('Número do documento (CI ou CNH) é obrigatório');
      return false;
    }

    if (!formData.issuing_org.trim()) {
      setError('Órgão emissor é obrigatório');
      return false;
    }

    if (!formData.issuing_date.trim()) {
      setError('Data de emissão é obrigatória');
      return false;
    }
    
    if (!formData.phone.trim()) {
      setError('Telefone é obrigatório');
      return false;
    }
    
    const pwdErr = validateNewPassword(formData.password);
    if (pwdErr) {
      setError(pwdErr);
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }
    
    if (!formData.address.street.trim()) {
      setError('Endereço é obrigatório');
      return false;
    }
    
    if (!formData.address.city.trim()) {
      setError('Cidade é obrigatória');
      return false;
    }
    
    if (!formData.address.state.trim()) {
      setError('Estado é obrigatório');
      return false;
    }
    
    if (!acceptedTerms) {
      setError('Você deve aceitar os termos de uso');
      return false;
    }
    
    return true;
  }, [formData, validateCPF, acceptedTerms]);

  const handleEmailSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validateEmailStep()) return;

    setLoading(true);
    setError('');
    setDevConfirmationUrl('');

    const email = formData.email.trim().toLowerCase();
    try {
      const { data, error: startError } = await apiClient.registerStart(email, emailConfirm.trim().toLowerCase());
      if (startError) {
        setError(startError);
      } else {
        setFormData((prev) => ({ ...prev, email }));
        if (data?.dev_confirmation_url) {
          setDevConfirmationUrl(data.dev_confirmation_url);
        }
        setStep('pending');
      }
    } catch (err) {
      setError('Erro ao enviar e-mail. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [formData.email, emailConfirm, validateEmailStep]);

  const handleResendEmail = useCallback(async () => {
    const email = formData.email.trim().toLowerCase();
    if (!email) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: resendError } = await apiClient.registerResend(email);
      if (resendError) {
        setError(resendError);
      } else if (data?.dev_confirmation_url) {
        setDevConfirmationUrl(data.dev_confirmation_url);
      }
    } catch (err) {
      setError('Erro ao reenviar e-mail.');
    } finally {
      setLoading(false);
    }
  }, [formData.email]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!activeToken) {
      setError('Link de confirmação inválido. Comece o cadastro informando seu e-mail.');
      setStep('email');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: authError } = await apiClient.registerComplete(activeToken, {
        password: formData.password,
        name: formData.name,
        mother_name: formData.mother_name || '',
        father_name: formData.father_name || '',
        birth_date: formData.birth_date || '',
        cpf: formData.cpf?.replace(/[^\d]/g, '') || '',
        social_name: formData.social_name || '',
        use_social_name: !!formData.use_social_name,
        identity_type: formData.identity_type || 'CI',
        identity_number: formData.identity_number || '',
        issuing_org: formData.issuing_org || '',
        issuing_date: formData.issuing_date || '',
        phone: formData.phone || '',
        address: formData.address || {}
      });

      if (authError) {
        setError(authError);
      } else if (data?.message) {
        onRegistrationComplete?.(data);
      }
    } catch (err) {
      setError('Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [activeToken, formData, validateForm, onRegistrationComplete]);

  const formatCPF = useCallback((value) => {
    const numbers = value.replace(/[^\d]/g, '');
    return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }, []);

  const formatPhone = useCallback((value) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    } else {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
  }, []);

  const formatZipCode = useCallback((value) => {
    const numbers = value.replace(/[^\d]/g, '');
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
  }, []);

  if (loading || tokenValidating) {
    return (
      <div style={styles.authContainer}>
        <div style={{ ...styles.authBox, maxWidth: '520px', textAlign: 'center' }}>
          <LoadingSpinner text={tokenValidating ? 'Validando link do e-mail...' : 'Processando...'} />
          {tokenValidating && (
            <p style={{ fontSize: 14, color: '#6B7280', marginTop: 8 }}>
              Aguarde alguns segundos. Se nada aparecer, solicite um novo link de cadastro.
            </p>
          )}
        </div>
      </div>
    );
  }

  const headerBlock = (
    <>
      <h1 style={{ ...styles.authTitle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
        {(icon?.startsWith('http') || icon?.startsWith('/')) && (
          <img src={icon} alt="" style={{ width: 80, height: 80, objectFit: 'contain' }} />
        )}
        <span>{systemName}</span>
      </h1>
      <p style={styles.authSubtitle}>
        {step === 'email' ? 'Cadastro de Candidato — passo 1' : step === 'pending' ? 'Confirme seu e-mail' : 'Cadastro de Candidato — complete seus dados'}
      </p>
    </>
  );

  if (registrationBlocked) {
    return (
      <div style={styles.authContainer}>
        <div style={{ ...styles.authBox, maxWidth: '520px' }}>
          <h1 style={styles.authTitle}>{systemName}</h1>
          <Alert
            message="Cadastro de candidatos não está disponível neste endereço. Acesse o site da instituição onde deseja se inscrever."
            type="info"
          />
          <button
            type="button"
            onClick={() => setView('login')}
            style={{ ...styles.button, ...styles.buttonPrimary, width: '100%', marginTop: 16 }}
          >
            Voltar ao login
          </button>
        </div>
      </div>
    );
  }

  if (step === 'email') {
    return (
      <div style={styles.authContainer}>
        <div style={{ ...styles.authBox, maxWidth: '520px' }}>
          {headerBlock}
          {error && <Alert message={error} type="error" onClose={() => setError('')} />}
          <form onSubmit={handleEmailSubmit} style={styles.form} autoComplete="off">
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 16 }}>
              Informe seu e-mail. Enviaremos um link para você continuar o cadastro com segurança.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={styles.label}>E-mail *</label>
              <input
                type="email"
                name="candidate-email"
                autoComplete="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                style={styles.input}
                placeholder="seu@email.com"
                required
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={styles.label}>Confirmar e-mail *</label>
              <input
                type="email"
                name="candidate-email-confirm"
                autoComplete="email"
                value={emailConfirm}
                onChange={(e) => setEmailConfirm(e.target.value.toLowerCase())}
                style={styles.input}
                placeholder="Digite o e-mail novamente"
                required
              />
            </div>
            <button type="submit" style={{ ...styles.button, ...styles.buttonPrimary, width: '100%' }}>
              Enviar link de confirmação
            </button>
          </form>
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button onClick={() => setView('login')} style={{ background: 'none', border: 'none', color: '#3B82F6', cursor: 'pointer', fontSize: 14 }}>
              ← Voltar para login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'pending') {
    return (
      <div style={styles.authContainer}>
        <div style={{ ...styles.authBox, maxWidth: '520px' }}>
          {headerBlock}
          {error && <Alert message={error} type="error" onClose={() => setError('')} />}
          <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, margin: 0 }}>
            Enviamos um link para <strong>{formData.email}</strong>.
          </p>
          <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, marginTop: 10 }}>
            Abra o e-mail e clique no link para continuar o cadastro. O link expira em <strong>48 horas</strong>.
          </p>
          <EmailInboxHint />
          {devConfirmationUrl && (
            <div style={{ marginTop: 16, padding: 12, background: '#FEF3C7', borderRadius: 8, fontSize: 12, wordBreak: 'break-all' }}>
              <strong>Ambiente de teste:</strong> use este link se o e-mail não chegar —{' '}
              <a href={devConfirmationUrl}>{devConfirmationUrl}</a>
            </div>
          )}
          <button
            type="button"
            onClick={handleResendEmail}
            style={{ ...styles.button, ...styles.buttonSecondary, width: '100%', marginTop: 20 }}
          >
            Reenviar e-mail
          </button>
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button onClick={() => { setStep('email'); setError(''); }} style={{ background: 'none', border: 'none', color: '#3B82F6', cursor: 'pointer', fontSize: 14 }}>
              Usar outro e-mail
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner text="Criando sua conta..." />;
  }

  return (
    <div style={styles.authContainer}>
      <div style={{ ...styles.authBox, maxWidth: '700px' }}>
        <h1 style={{ ...styles.authTitle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {(icon?.startsWith('http') || icon?.startsWith('/')) && (
            <img src={icon} alt="" style={{ width: 80, height: 80, objectFit: 'contain' }} />
          )}
          <span>{systemName}</span>
        </h1>
        <p style={styles.authSubtitle}>Registro de Candidato</p>
        
        {error && (
          <Alert 
            message={error} 
            type="error" 
            onClose={() => setError('')} 
          />
        )}

        <div style={{ marginBottom: 20, padding: 12, background: '#EFF6FF', borderRadius: 8, fontSize: 14, color: '#1E40AF' }}>
          E-mail confirmado: <strong>{formData.email}</strong>
        </div>
        
        <form onSubmit={handleSubmit} style={styles.form} autoComplete="off">
          {/* Dados Pessoais */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px', color: '#374151', fontSize: '18px' }}>
              📋 Dados Pessoais
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={styles.label}>Nome Completo *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={styles.input}
                  placeholder="Seu nome completo"
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Nome da Mãe *</label>
                <input
                  type="text"
                  value={formData.mother_name}
                  onChange={(e) => handleInputChange('mother_name', e.target.value)}
                  style={styles.input}
                  placeholder="Nome completo da mãe"
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Nome do Pai</label>
                <input
                  type="text"
                  value={formData.father_name}
                  onChange={(e) => handleInputChange('father_name', e.target.value)}
                  style={styles.input}
                  placeholder="Nome completo do pai (opcional)"
                />
              </div>
              <div>
                <label style={styles.label}>Nome Social</label>
                <input
                  type="text"
                  value={formData.social_name}
                  onChange={(e) => handleInputChange('social_name', e.target.value)}
                  style={styles.input}
                  placeholder="Nome pelo qual deseja ser chamado (opcional)"
                />
              </div>
              <div>
                <label style={styles.label}>Data de Nascimento *</label>
                <input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => handleInputChange('birth_date', e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            {formData.social_name.trim() && (
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px', padding: '12px', backgroundColor: '#F0FDF4', borderRadius: '8px', border: '1px solid #BBF7D0' }}>
                <input
                  type="checkbox"
                  id="use_social_name"
                  checked={formData.use_social_name}
                  onChange={(e) => handleInputChange('use_social_name', e.target.checked)}
                  style={{ width: '16px', height: '16px', marginRight: '12px' }}
                />
                <label htmlFor="use_social_name" style={{ fontSize: '14px', color: '#166534' }}>
                  Desejo que meu nome social seja utilizado em todas as publicações do sistema (relatórios, listas, etc.)
                </label>
              </div>
            )}
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
              <div>
                <label style={styles.label}>CPF *</label>
                <input
                  type="text"
                  value={formData.cpf}
                  onChange={(e) => handleInputChange('cpf', formatCPF(e.target.value))}
                  style={styles.input}
                  placeholder="000.000.000-00"
                  maxLength="14"
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Documento (CI ou CNH) *</label>
                <select
                  value={formData.identity_type}
                  onChange={(e) => handleInputChange('identity_type', e.target.value)}
                  style={styles.select}
                  required
                >
                  <option value="CI">CI</option>
                  <option value="CNH">CNH</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Número do Documento *</label>
                <input
                  type="text"
                  value={formData.identity_number}
                  onChange={(e) => handleInputChange('identity_number', e.target.value)}
                  style={styles.input}
                  placeholder={formData.identity_type === 'CNH' ? 'Número da CNH' : 'Número da CI'}
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Órgão Emissor *</label>
                <input
                  type="text"
                  value={formData.issuing_org}
                  onChange={(e) => handleInputChange('issuing_org', e.target.value)}
                  style={styles.input}
                  placeholder="Ex: SSP, DETRAN"
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Data de Emissão *</label>
                <input
                  type="date"
                  value={formData.issuing_date}
                  onChange={(e) => handleInputChange('issuing_date', e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
              <div>
                <label style={styles.label}>Telefone *</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', formatPhone(e.target.value))}
                  style={styles.input}
                  placeholder="(00) 00000-0000"
                  required
                />
              </div>
            </div>
            
            {/* E-mail confirmado no passo anterior — não editável aqui */}
          </div>

          {/* Senha */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px', color: '#374151', fontSize: '18px' }}>
              🔐 Senha
            </h3>
            <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px', lineHeight: 1.5 }}>
              {PASSWORD_POLICY_HINT}
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={styles.label}>Senha *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="new-password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    style={{ ...styles.input, paddingRight: '40px' }}
                    placeholder="Mínimo 8 caracteres"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    tabIndex={-1}
                    style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6B7280' }}
                    title={showPassword ? 'Ocultar senha' : 'Ver senha'}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {showPassword ? <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /> : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>}
                    </svg>
                  </button>
                </div>
              </div>
              <div>
                <label style={styles.label}>Confirmar Senha *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirm-new-password"
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    style={{ ...styles.input, paddingRight: '40px' }}
                    placeholder="Digite a senha novamente"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(v => !v)}
                    tabIndex={-1}
                    style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#6B7280' }}
                    title={showConfirmPassword ? 'Ocultar senha' : 'Ver senha'}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      {showConfirmPassword ? <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /> : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>}
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Endereço */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px', color: '#374151', fontSize: '18px' }}>
              🏠 Endereço
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
              <div>
                <label style={styles.label}>Rua *</label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => handleInputChange('address.street', e.target.value)}
                  style={styles.input}
                  placeholder="Nome da rua"
                  required
                />
              </div>
              
              <div>
                <label style={styles.label}>Número</label>
                <input
                  type="text"
                  value={formData.address.number}
                  onChange={(e) => handleInputChange('address.number', e.target.value)}
                  style={styles.input}
                  placeholder="Nº"
                />
              </div>
            </div>
            
            <div style={{ marginTop: '16px' }}>
              <label style={styles.label}>Complemento</label>
              <input
                type="text"
                value={formData.address.complement}
                onChange={(e) => handleInputChange('address.complement', e.target.value)}
                style={styles.input}
                placeholder="Apartamento, bloco, etc."
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '16px' }}>
              <div>
                <label style={styles.label}>Bairro</label>
                <input
                  type="text"
                  value={formData.address.neighborhood}
                  onChange={(e) => handleInputChange('address.neighborhood', e.target.value)}
                  style={styles.input}
                  placeholder="Nome do bairro"
                />
              </div>
              
              <div>
                <label style={styles.label}>Cidade *</label>
                <input
                  type="text"
                  value={formData.address.city}
                  onChange={(e) => handleInputChange('address.city', e.target.value)}
                  style={styles.input}
                  placeholder="Nome da cidade"
                  required
                />
              </div>
              
              <div>
                <label style={styles.label}>Estado *</label>
                <select
                  value={formData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  style={styles.select}
                  required
                >
                  <option value="">Selecione...</option>
                  <option value="AC">Acre</option>
                  <option value="AL">Alagoas</option>
                  <option value="AP">Amapá</option>
                  <option value="AM">Amazonas</option>
                  <option value="BA">Bahia</option>
                  <option value="CE">Ceará</option>
                  <option value="DF">Distrito Federal</option>
                  <option value="ES">Espírito Santo</option>
                  <option value="GO">Goiás</option>
                  <option value="MA">Maranhão</option>
                  <option value="MT">Mato Grosso</option>
                  <option value="MS">Mato Grosso do Sul</option>
                  <option value="MG">Minas Gerais</option>
                  <option value="PA">Pará</option>
                  <option value="PB">Paraíba</option>
                  <option value="PR">Paraná</option>
                  <option value="PE">Pernambuco</option>
                  <option value="PI">Piauí</option>
                  <option value="RJ">Rio de Janeiro</option>
                  <option value="RN">Rio Grande do Norte</option>
                  <option value="RS">Rio Grande do Sul</option>
                  <option value="RO">Rondônia</option>
                  <option value="RR">Roraima</option>
                  <option value="SC">Santa Catarina</option>
                  <option value="SP">São Paulo</option>
                  <option value="SE">Sergipe</option>
                  <option value="TO">Tocantins</option>
                </select>
              </div>
            </div>
            
            <div style={{ marginTop: '16px' }}>
              <label style={styles.label}>CEP</label>
              <input
                type="text"
                value={formData.address.zipCode}
                onChange={(e) => handleInputChange('address.zipCode', formatZipCode(e.target.value))}
                style={styles.input}
                placeholder="00000-000"
                maxLength="9"
              />
            </div>
          </div>

          {/* Termos */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                style={{ width: '16px', height: '16px' }}
              />
              <label style={{ fontSize: '14px', color: '#374151' }}>
                Li e aceito os{' '}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3B82F6',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '14px'
                  }}
                >
                  termos de uso
                </button>
              </label>
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              ...styles.buttonPrimary,
              width: '100%',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Criando conta...' : 'Criar Conta de Candidato'}
          </button>
        </form>
        
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <button
            onClick={() => setView('login')}
            style={{
              background: 'none',
              border: 'none',
              color: '#3B82F6',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Já tem conta? Faça login
          </button>
        </div>
      </div>

      {/* Modal de Termos */}
      <Modal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title="Termos de Uso"
      >
        <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '16px' }}>
          {(() => {
            const text = termsOfUse || 'Carregando...';
            // Converte HTML antigo em quebras de linha (conteúdo salvo antes da mudança)
            const normalized = text
              .replace(/<\/h[1-6]>\s*/gi, '\n\n')
              .replace(/<\/p>\s*<p>/gi, '\n\n')
              .replace(/<\/p>\s*/gi, '\n\n')
              .replace(/<br\s*\/?>\s*/gi, '\n')
              .replace(/<[^>]+>/g, '');
            const lines = normalized.split(/\r?\n/);
            return lines.map((line, i) => (
              <div key={i} style={{ marginBottom: line.trim() ? '10px' : '6px', lineHeight: 1.5 }}>
                {line}
              </div>
            ));
          })()}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button
            onClick={() => setShowTermsModal(false)}
            style={{
              ...styles.button,
              ...styles.buttonPrimary
            }}
          >
            Entendi
          </button>
        </div>
      </Modal>
    </div>
  );
});












